import Input from '../orm/Input'
import Model from '../orm/Model'

import Attendance from './Attendance'
import Course from './Course'
import Device from './Device'
import Event from './Event'
import Excuse from './Excuse'
import Team from './Team'
import StudentCourse from './StudentCourse'
import StudentEvent from './StudentEvent'
import StudentTeam from './StudentTeam'

import { isEmpty } from '@/utilities'

const studentInput = new Input('StudentInput!', [
    'first_name',
    'last_name',
    'preferred_name',
    'email',
    'phone',
    'age',
    'pid_number',
    'zip',
    'date_of_birth',
    'is_veteran',
    'housing',
    'employment',
    'sexual_identity',
    'gender',
    'sex',
    'race',
    'school_identifier',
])

export default class Student extends Model {
    static entity = 'students'

    static fields() {
        return {
            id: this.attr(null),
            uuid: this.string(''),
            first_name: this.string(''),
            last_name: this.string(''),
            preferred_name: this.attr(null),
            token: this.attr(null),
            email: this.attr(null),
            phone: this.attr(null),
            school_identifier: this.attr(null),
            age: this.attr(null),
            pid_number: this.attr(null),
            zip: this.attr(null),
            date_of_birth: this.attr(null),
            is_veteran: this.attr(null),
            housing: this.attr(null),
            employment: this.attr(null),
            sexual_identity: this.attr(null),
            gender: this.attr(null),
            sex: this.attr(null),
            race: this.attr(null),
            weekly_apa: this.number(0),
            semester_apa: this.number(0),
            offline_code: this.attr(null),
            activity_status: this.number(0),
            inactive_date: this.attr(null),
            deleted_at: this.attr(null),
            last_seen: this.attr(null),
            courses: this.belongsToMany(
                Course,
                StudentCourse,
                'student_id',
                'course_id'
            ),
            events: this.belongsToMany(
                Event,
                StudentEvent,
                'student_id',
                'event_id'
            ),
            device_id: this.number(0),
            device: this.belongsTo(Device, 'device_id'),

            teams: this.belongsToMany(
                Team,
                StudentTeam,
                'student_id',
                'team_id'
            ),

            past_teams: this.array([], ['id', 'uuid', 'name']),

            excuses: this.hasMany(Excuse, 'student_id'),

            attendance: this.hasMany(Attendance, 'student_id'),
        }
    }

    static graphql = {
        all: {
            paginated: true,
            parameters: {
                page: 'Int',
                per_page: 'Int',
                sort_by: 'String',
                sort_dir: 'String',
                state: 'String',
                missing: 'Boolean',
                relation: 'String',
                relation_uuid: 'String',
            },
        },

        relation: {
            paginated: true,
            parameters: {
                page: 'Int',
                per_page: 'Int',
                sort_by: 'String',
                sort_dir: 'String',
                state: 'String',
                relation: 'String',
                relation_uuid: 'String',
            },
            exclude: [
                'token',
                'phone',
                'school_identifier',
                'last_seen',
                'device_id',
            ],
        },

        search: {
            paginated: true,
            parameters: {
                query: 'String',
                per_page: 'Int!',
                unscoped: 'Boolean',
                relation: 'String',
                relation_uuid: 'String',
            },
        },

        one: {
            type: 'student',
            parameters: {
                uuid: 'String!',
                courses: {
                    week: 'String',
                    attendance: {
                        week: 'String',
                    },
                },
                events: {
                    state: 'String',
                },
            },
            include: {
                courses: {
                    location: true,
                    attendance: {
                        overrides: {
                            user: true,
                        },
                        coordinates: true,
                    },
                },
                events: {
                    location: true,
                    attendance: {
                        overrides: {
                            user: true,
                        },
                        coordinates: true,
                    },
                    in_waitlist: true,
                },
                device: true,
                teams: true,
                past_teams: true,
                excuses: true,
            },
            exclude: {
                events: ['attending_count', 'waitlist_count'],
            },
        },

        create: {
            type: 'createStudent',
            parameters: {
                input: studentInput,
            },
        },

        update: {
            type: 'updateStudent',
            parameters: {
                uuid: 'String!',
                input: studentInput,
            },
        },

        remove: {
            type: 'removeStudent',
            parameters: {
                id: 'Int!',
            },
        },

        restore: {
            type: 'restoreStudent',
            parameters: {
                id: 'Int!',
            },
        },

        resetToken: {
            type: 'resetStudentToken',
            parameters: {
                uuid: 'String!',
                send_email: 'Boolean',
            },
        },

        inactivate: {
            type: 'inactivateStudent',
            parameters: {
                uuid: 'String!',
                activity_status: 'Int!',
            },
        },
    }

    static $one(params) {
        if (!params.week) {
            params.week = new Date()
        }
        if (!params.state) {
            params.state = 'active'
        }
        return this.$fetchOne(params)
    }

    static async $search(params) {
        if (isEmpty(params.query)) return
        if (isEmpty(params.per_page)) {
            params.per_page = 5
        }
        params.unscoped = true
        const { data } = await this.$fetch({
            graphql: 'search',
            variables: params,
        })

        return data
    }
}
